import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Viewer as CesiumViewer } from 'cesium';
import { TrackPosition, Geofence, Mission } from '@airrecon/shared';
import { BasemapId, DEFAULT_BASEMAP_ID } from '../components/map/basemaps';

interface MapLayers {
  liveTracks: boolean;
  historicalTracks: boolean;
  airspace: boolean;
  geofences: boolean;
  missions: boolean;
  facilities: boolean;
  heatmap: boolean;
  dronetagCloud: boolean;
  openSky: boolean;
  sensors: boolean;
}

/** Snapshot of a clicked ADS-B aircraft, copied at click time so the
 *  popup keeps showing meaningful data even if the aircraft drops out
 *  of the next poll's radius. */
export interface ADSBAircraftSnapshot {
  hex: string;
  flight: string | null;
  registration: string | null;
  aircraftType: string | null;
  latitude: number;
  longitude: number;
  altitudeBaro: number | null;
  altitudeGeom: number | null;
  groundSpeed: number | null;
  track: number | null;
  baroRate: number | null;
  squawk: string | null;
  category: string | null;
  onGround: boolean;
  airline: string | null;
  capturedAt: number; // ms since epoch
}

export interface SensorSnapshot {
  id: string;
  name: string;
  status: string;
  latitude: number;
  longitude: number;
  altitudeMsl: number | null;
}

export interface MapIconMarkerSnapshot {
  id: string;
  kind: 'DRONE' | 'PILOT' | 'HOME';
  name: string;
  latitude: number;
  longitude: number;
  altitudeMsl: number | null;
  detail?: string | null;
  trackId?: string | null;
}

interface TimeRange {
  start: Date;
  end: Date;
}

interface MapState {
  // View state
  viewMode: '3D' | '2D';
  mapStyle: 'SATELLITE' | 'TERRAIN' | 'DARK' | 'LIGHT';

  // Layers
  layers: MapLayers;
  toggleLayer: (layer: keyof MapLayers) => void;
  setLayers: (layers: Partial<MapLayers>) => void;

  // Live tracks
  liveTracks: Map<string, TrackPosition>;
  liveTrackTrails: Map<string, Array<{ longitude: number; latitude: number; altitudeMSL: number }>>;
  setLiveTrack: (track: TrackPosition) => void;
  removeLiveTrack: (trackId: string) => void;
  clearLiveTracks: () => void;

  // Selected track
  selectedTrackId: string | null;
  setSelectedTrack: (trackId: string | null) => void;

  // Selected ADS-B aircraft (full data so the popup doesn't have to
  // reach back into the Cesium DataSource — the hook keeps this in
  // sync as new ADS-B polls land).
  selectedAircraft: ADSBAircraftSnapshot | null;
  setSelectedAircraft: (aircraft: ADSBAircraftSnapshot | null) => void;

  // Selected sensor marker. Sensor map labels are intentionally rendered
  // as a DOM tooltip after click instead of always-on Cesium labels, which
  // avoids Cesium's colored label speckle on some GPUs / zoom levels.
  selectedSensor: SensorSnapshot | null;
  setSelectedSensor: (sensor: SensorSnapshot | null) => void;
  sensorMarkers: SensorSnapshot[];
  setSensorMarkers: (sensors: SensorSnapshot[]) => void;
  replayIconMarkers: MapIconMarkerSnapshot[];
  setReplayIconMarkers: (markers: MapIconMarkerSnapshot[]) => void;

  // Historical playback
  isPlaying: boolean;
  playbackSpeed: number;
  playbackTime: Date | null;
  timeRange: TimeRange | null;
  setPlayback: (playing: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  setPlaybackTime: (time: Date | null) => void;
  setTimeRange: (range: TimeRange | null) => void;

  // Geofences
  geofences: Geofence[];
  setGeofences: (geofences: Geofence[]) => void;
  selectedGeofenceId: string | null;
  setSelectedGeofence: (id: string | null) => void;

  // Missions
  missions: Mission[];
  setMissions: (missions: Mission[]) => void;
  selectedMissionId: string | null;
  setSelectedMission: (id: string | null) => void;

  // Drawing mode
  drawingMode: 'NONE' | 'GEOFENCE' | 'MISSION_ROUTE' | 'MEASURE';
  setDrawingMode: (mode: 'NONE' | 'GEOFENCE' | 'MISSION_ROUTE' | 'MEASURE') => void;
  drawnCoordinates: [number, number][];
  addDrawnCoordinate: (coord: [number, number]) => void;
  clearDrawnCoordinates: () => void;

  // Camera
  cameraPosition: { longitude: number; latitude: number; height: number } | null;
  setCameraPosition: (pos: { longitude: number; latitude: number; height: number }) => void;
  flyTo: (longitude: number, latitude: number, height?: number) => void;
  flyToTarget: { longitude: number; latitude: number; height: number } | null;

  // Dronetag Cloud
  dronetagApiToken: string | null;
  setDronetagApiToken: (token: string | null) => void;

  // Flight replay
  replayTrackId: string | null;
  setReplayTrackId: (trackId: string | null) => void;

  // Viewer instance (for direct camera manipulation from UI controls)
  viewer: CesiumViewer | null;
  setViewer: (viewer: CesiumViewer | null) => void;

  // Basemap selection — shared across all Cesium viewers in the app
  // (live map, replay, historical, and the analytics heatmap).
  basemapId: BasemapId;
  setBasemapId: (id: BasemapId) => void;
}

export const useMapStore = create<MapState>()(
  persist(
    (set) => ({
  // View state
  viewMode: '3D',
  mapStyle: 'SATELLITE',

  // Layers
  layers: {
    liveTracks: true,
    historicalTracks: false,
    airspace: true,
    geofences: true,
    missions: true,
    facilities: true,
    heatmap: false,
    dronetagCloud: false,
    openSky: false,
    // Sensor coverage markers — default on so operators can see at a
    // glance which sensors are placed and their online status.
    sensors: true,
  },

  toggleLayer: (layer) => {
    set((state) => ({
      layers: {
        ...state.layers,
        [layer]: !state.layers[layer],
      },
    }));
  },

  setLayers: (layers) => {
    set((state) => ({
      layers: { ...state.layers, ...layers },
    }));
  },

  // Live tracks
  liveTracks: new Map(),
  liveTrackTrails: new Map(),

  setLiveTrack: (track) => {
    set((state) => {
      const newTracks = new Map(state.liveTracks);
      newTracks.set(track.trackId, track);

      // Append to trail history (capped at 300 points per track)
      const MAX_TRAIL = 300;
      const newTrails = new Map(state.liveTrackTrails);
      const existing = newTrails.get(track.trackId) || [];
      const point = { longitude: track.longitude, latitude: track.latitude, altitudeMSL: track.altitudeMSL };
      // Only add if position actually changed (avoid duplicate stacked points)
      const last = existing[existing.length - 1];
      if (!last || last.latitude !== point.latitude || last.longitude !== point.longitude) {
        const updated = existing.length >= MAX_TRAIL
          ? [...existing.slice(existing.length - MAX_TRAIL + 1), point]
          : [...existing, point];
        newTrails.set(track.trackId, updated);
      }

      return { liveTracks: newTracks, liveTrackTrails: newTrails };
    });
  },

  removeLiveTrack: (trackId) => {
    set((state) => {
      const newTracks = new Map(state.liveTracks);
      newTracks.delete(trackId);
      const newTrails = new Map(state.liveTrackTrails);
      newTrails.delete(trackId);
      return { liveTracks: newTracks, liveTrackTrails: newTrails };
    });
  },

  clearLiveTracks: () => {
    // Keep trails across poll cycles — only the current position map resets.
    // Trails persist until the track disappears from the stale window entirely.
    // MapPage prunes stale trails in its useEffect.
    set({ liveTracks: new Map() });
  },

  // Selected track
  selectedTrackId: null,
  setSelectedTrack: (trackId) => set({ selectedTrackId: trackId }),

  // Selected aircraft popover (ADS-B click target)
  selectedAircraft: null,
  setSelectedAircraft: (aircraft) => set({ selectedAircraft: aircraft }),

  // Selected sensor tooltip
  selectedSensor: null,
  setSelectedSensor: (sensor) => set({ selectedSensor: sensor }),
  sensorMarkers: [],
  setSensorMarkers: (sensors) => {
    set((state) => {
      if (
        state.sensorMarkers.length === sensors.length &&
        state.sensorMarkers.every((sensor, index) => sensor === sensors[index])
      ) {
        return state;
      }
      return { sensorMarkers: sensors };
    });
  },
  replayIconMarkers: [],
  setReplayIconMarkers: (markers) => {
    set((state) => {
      if (
        state.replayIconMarkers.length === markers.length &&
        state.replayIconMarkers.every((marker, index) => marker === markers[index])
      ) {
        return state;
      }
      return { replayIconMarkers: markers };
    });
  },

  // Historical playback
  isPlaying: false,
  playbackSpeed: 1,
  playbackTime: null,
  timeRange: null,

  setPlayback: (playing) => {
    set((state) => (state.isPlaying === playing ? state : { isPlaying: playing }));
  },
  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
  setPlaybackTime: (time) => {
    set((state) => {
      const currentTime = state.playbackTime?.getTime() ?? null;
      const nextTime = time?.getTime() ?? null;
      return currentTime === nextTime ? state : { playbackTime: time };
    });
  },
  setTimeRange: (range) => {
    set((state) => {
      const current = state.timeRange;
      if (
        (!current && !range) ||
        (current &&
          range &&
          current.start.getTime() === range.start.getTime() &&
          current.end.getTime() === range.end.getTime())
      ) {
        return state;
      }
      return { timeRange: range };
    });
  },

  // Geofences
  geofences: [],
  setGeofences: (geofences) => set({ geofences }),
  selectedGeofenceId: null,
  setSelectedGeofence: (id) => set({ selectedGeofenceId: id }),

  // Missions
  missions: [],
  setMissions: (missions) => set({ missions }),
  selectedMissionId: null,
  setSelectedMission: (id) => set({ selectedMissionId: id }),

  // Drawing mode
  drawingMode: 'NONE',
  setDrawingMode: (mode) => set({ drawingMode: mode, drawnCoordinates: [] }),
  drawnCoordinates: [],
  addDrawnCoordinate: (coord) => {
    set((state) => ({
      drawnCoordinates: [...state.drawnCoordinates, coord],
    }));
  },
  clearDrawnCoordinates: () => set({ drawnCoordinates: [] }),

  // Camera
  cameraPosition: null,
  setCameraPosition: (pos) => {
    set((state) => {
      const current = state.cameraPosition;
      if (
        current &&
        current.longitude === pos.longitude &&
        current.latitude === pos.latitude &&
        current.height === pos.height
      ) {
        return state;
      }
      return { cameraPosition: pos };
    });
  },
  flyToTarget: null,
  flyTo: (longitude, latitude, height = 50000) => {
    set({ flyToTarget: { longitude, latitude, height } });
    // Clear target after a short delay
    setTimeout(() => set({ flyToTarget: null }), 100);
  },

  // Dronetag Cloud
  dronetagApiToken: null,
  setDronetagApiToken: (token) => set({ dronetagApiToken: token }),

  // Flight replay
  replayTrackId: null,
  setReplayTrackId: (trackId) => set({ replayTrackId: trackId }),

  // Viewer instance
  viewer: null,
  setViewer: (viewer) => {
    set((state) => (state.viewer === viewer ? state : { viewer }));
  },

  // Basemap (persisted so the user's choice survives refresh)
  basemapId: DEFAULT_BASEMAP_ID,
  setBasemapId: (id) => set({ basemapId: id }),
}),
    {
      name: 'airrecon-map',
      partialize: (state) => ({
        dronetagApiToken: state.dronetagApiToken,
        basemapId: state.basemapId,
      }),
    }
  )
);
